<template>
  <div class="view-container">
    <div class="extended-filter-block">
      <h2>Сотрудники</h2>
      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined @click="showMobileFilterModal = true" style="color: rgba(0, 0, 0, 0.45)" />
          <div v-if="filterString" class="dot"></div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="filter-block">
        <div>
          <a-segmented v-model:value="groupFilter" :options="groupFilterData" />
        </div>
        <div class="search-tool-block">
            <a-select
                v-model:value="additionalFilters.groups"
                mode="multiple"
                :placeholder="'Выберите группы'"
                :not-found-content="'Нет данных'"
                :disabled="groupFilter !== 'По группам'"
                :loading="groupsLoading"
                @select="handleGroupSelect"
            >
              <a-select-option v-for="e in groups" :key="e.id" :value="e.id">
                {{ e.name }}
              </a-select-option>
            </a-select>
        </div>

        <div class="add-button">
          <a-button @click="addNewUser">
            <template #icon>
              <PlusOutlined />
            </template>
            Добавить
          </a-button>
        </div>
      </div>

      <a-table
          rowKey="id"
          :columns="columns"
          :data-source="userData"
          :scroll="{ x: 2000 }"
          :row-selection="{ selectedRowKeys: selectedUsers, onChange: onSelectChange }"
          :pagination="false"
          :loading="tableLoading"
          @change="handleTableChange"
          :table-layout="'fixed'"
      >
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'name'">
            <a class="staff-link" @click="router.push(`/staff/${record.id}/edit`)">{{ text }}</a>
          </template>

          <template v-if="column.dataIndex === 'groups'">
            <a-tag v-for="(group) in record.groups" :key="group.id">
              {{ group.name }}
            </a-tag>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <a-tag color="green" v-if="record.status === 'active'">
              Работает
            </a-tag>
            <a-tag color="red" v-else>
              В архиве
            </a-tag>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a-dropdown :trigger="['click']" placement="bottomLeft">
              <a-button type="text" @click.prevent>
                <MenuOutlined />
              </a-button>

              <template #overlay>
                <a-menu @click="({key}) => handleAction(key, record)">
                  <a-menu-item key="groups">Настроить группы</a-menu-item>
                  <a-menu-item key="reset_password">Сбросить пароль</a-menu-item>
                  <a-menu-item key="manage_access">Управлять доступами</a-menu-item>
                  <a-menu-item key="edit">Редактировать</a-menu-item>
                  <a-menu-item key="send_invite">Отправить приглашение</a-menu-item>
                  <a-menu-item v-if="record.status ==='active'" key="block">Архивировать</a-menu-item>
                  <a-menu-item v-else key="unblock">Разблокировать</a-menu-item>
                  <a-menu-item key="export">Экспортировать</a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </template>
        </template>

        <template #emptyText>
          <div class="no-data-wrapper">
            <img :src="NotFoundIcon" class="no-data-image" alt="Нет результатов"/>

            <div class="no-data-text">
              {{
                filterString
                    ? 'Нет данных для отображения. Измените фильтры или запрос.'
                    : 'Нет данных для отображения. Добавьте сотрудников, чтобы начать работу.'
              }}
            </div>

            <a-button
                v-if="filterString"
                @click="resetFilters"
            >
              <template #icon>
                <ReloadOutlined />
              </template>
              Сбросить фильтры
            </a-button>

            <a-button
                v-else
                @click="addNewUser"
            >
              <template #icon>
                <PlusOutlined />
              </template>
              Добавить
            </a-button>
          </div>
        </template>
      </a-table>

      <div class="bottom-container">
        <div class="action-block">
          <div class="action-select-wrapper">
            <a-select
                v-model:value="actionToApply"
                :placeholder="'Выберите действие'"
                style="width: 100%;"
                @change="() => handleAction(actionToApply)"
                :not-found-content="'Нет данных'"
                :disabled="!selectedUsers.length"
            >
              <a-select-option v-for="e in actions" :key="e.id" :value="e.id">
                {{ e.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>
        <a-pagination v-if="!hidePagination" v-model:current="currentPage"
                      :page-size="entitiesPerPage"
                      :buildOptionText="(count) => `${count.value} / стр`"
                      :show-total
                          ="total => `Всего ${total ?? 0} элементов`"
                      :total="totalUsers"
                      @change="handlePageChange"
        />
      </div>
    </div>

    <UsersFilterMobileForm
      v-if="showMobileFilterModal"
      :open="showMobileFilterModal"
      @close="showMobileFilterModal = false"
      :groups="groups"
    />

    <SyncGroupsForm
        v-if="currentAction === 'groups'"
        :open="currentAction === 'groups'"
        :group-list="groups"
        :user_groups="currentActionUser.groups"
        :user_id="currentActionUser.id"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <CopyUsersIntoGroup
        v-if="currentAction === 'copy_to_group'"
        :open="currentAction === 'copy_to_group'"
        :group-list="groups"
        :users="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <SendInviteForm
        v-if="currentAction === 'send_invite'"
        :open="currentAction === 'send_invite'"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <BlockUserForm
        v-if="currentAction === 'block' || currentAction === 'unblock'"
        :open="currentAction === 'block' || currentAction === 'unblock'"
        :type="currentAction"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <ResetPasswordForm
        v-if="currentAction === 'reset_password'"
        :open="currentAction === 'reset_password'"
        :currentActionUser="currentActionUser"
        :selectedUsers="selectedUsers"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <NewStaffForm
        v-if="currentAction === 'add_user'"
        :open="currentAction === 'add_user'"
        @close="clearCurrentUserAndAction"
        @userCreated="fetchUsers"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        @close="showOrderForm = false"
    />

    <ManageAccessForm
        :open="currentAction === 'manage_access'"
        v-if="currentAction === 'manage_access'"
        @close="clearCurrentUserAndAction"
        @showStaff="() => {
              selectedUsers = [];
              clearCurrentUserAndAction();
              resetFilters();
            }"
        @showOrderForm="() => {
              clearCurrentUserAndAction();
              showOrderForm = true;
            }"
        :userData="currentActionUser ? [currentActionUser.id] : selectedUsers"
        :name="currentActionUser ? currentActionUser.name : null"
        :mode="'staff'"
    />
  </div>
</template>

<script>
import {onBeforeMount, onBeforeUnmount, reactive, ref, watch} from "@vue/runtime-core";
import {h, onMounted} from "vue";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import {computed} from "@vue/reactivity";
import { SearchOutlined, SettingOutlined, PlusOutlined, ReloadOutlined, MenuOutlined} from '@ant-design/icons-vue'
import NewStaffForm from "@/views/Staff/NewStaffForm.vue";
import ManageAccessForm from "@/components/Forms/Access/ManageAccessForm.vue";
import { message } from 'ant-design-vue';
import NewOrderForm from "@/views/Orders/OrderForm.vue";
import ResetPasswordForm from "@/feature/users/forms/ResetPasswordForm.vue";
import BlockUserForm from "@/feature/users/forms/BlockUserForm.vue";
import SendInviteForm from "@/feature/users/forms/SendInviteForm.vue";
import CopyUsersIntoGroup from "@/feature/group-control/forms/CopyUsersIntoGroup.vue";
import SyncGroupsForm from "@/feature/group-control/forms/SyncGroupsForm.vue";
import UsersFilterMobileForm from "@/feature/users/UsersFilterMobileForm.vue";

export default {
  name: 'new-staff-page',
  components: {
    UsersFilterMobileForm,
    SyncGroupsForm,
    CopyUsersIntoGroup,
    SendInviteForm,
    BlockUserForm,
    ResetPasswordForm,
    NewOrderForm,
    ManageAccessForm,
    NewStaffForm, SearchOutlined, SettingOutlined, PlusOutlined, ReloadOutlined, MenuOutlined},
  setup() {

    const store = useStore(),
        router = useRouter();

    const showOrderForm = ref(false);
    const applyToAll = ref(false);
    const actionToApply = ref();
    const tableLoading = ref(true);
    const groupsLoading = ref(true);
    const showMobileFilterModal = ref(false);
    const showActionModal = ref(false);
    const modalActionLoading = ref(false);

    const passwordResetForm = ref({
      type: 'auto',
      password: '',
      reset_required: false,
    });

    const statusError = ref({
      password: false,
    });

    const allGroups = computed(() => store.state.group.groups);
    const groupToCopyIn = ref();
    const groupFilterData = reactive(['Все', 'Без группы', 'По группам']);
    const groups = ref([]);
    const groupFilter = ref(groupFilterData[0]);

    const filters = computed(() => store.state.users.filters);
    const filterString = computed(() => store.state.users.filterString);

    const additionalFilters = reactive({
      without_group_only: false,
      groups: filters.value?.groups ?? [],
    });

    const selectedUsers = ref([]);
    const users = computed(() => store.state.users.users);
    const userData = computed(() => {
      return users.value.map((staff) => {
        return {
          id: staff.id,
          name: staff.name,
          email: staff.email,
          job_title: staff?.job_title ?? '',
          division: staff?.division ?? '',
          telephone: staff?.telephone ?? '',
          status: staff?.is_active ? 'active' : 'not_active',
          online_from: staff.online_from,
          created_at: staff.created_at,
          groups: staff.groups,
        };
      })
    });

    const currentActionUser = ref({
      id: null,
      name: '',
      groups: [],
    });

    const currentAction = ref();

    const showFilterModal = ref(false);

    const entitiesPerPage = ref(10);
    const currentPage = ref(1);
    const totalUsers = computed(() => store.state.users.totalUsers);
    const specialStaff = computed(() => store.state.users.specialStaff);
    const blockedStaff = computed(() => store.state.users.showBlocked);
    const hidePagination = ref(false);

    const filtersPopoverOpen = ref(false);

    const columns = computed(() => [
      {
        dataIndex: 'action',
        title: () =>
            h(SettingOutlined, {
              style: {
                color: '#999',
              },
            }),
        width: 30,
        align: 'center',
      },
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
        sorter: true,
      },
      {
        title: 'ФИО',
        dataIndex: 'name',
        sorter: true,
        width: 250,
      },
      {
        title: 'Должность',
        dataIndex: 'job_title',
        sorter: true,
        width: 150,
      },
      {
        title: 'Группы',
        dataIndex: 'groups',
        width: 250,
      },
      {
        title: 'Почта',
        dataIndex: 'email',
        sorter: true,
        width: 180,
      },
      {
        title: 'Телефон',
        dataIndex: 'telephone',
        sorter: true,
        width: 150,
      },
      {
        title: 'Отдел',
        dataIndex: 'department',
        sorter: true,
        width: 150,
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: 150,
      },
      {
        title: 'Дата добавления',
        dataIndex: 'created_at',
        sorter: true,
        width: 150,
      },
      {
        title: 'Последний вход',
        dataIndex: 'online_from',
        sorter: true,
        width: 150,
      },
    ]);

    const actions = [
      {
        id: 'copy_to_group',
        name: 'Копировать в группу'
      },
      {
        id: 'reset_password',
        name: 'Сбросить пароль'
      },
      {
        id: 'manage_access',
        name: 'Управлять доступами'
      },
      {
        id: 'send_invite',
        name: 'Отправить приглашения'
      },
      {
        id: 'block',
        name: 'Архивировать'
      },
      {
        id: 'export',
        name: 'Экспортировать'
      },
    ];

    const fetchUsers = () => {
      tableLoading.value = true;

      store.dispatch("users/getStaff", {
        special_staff: specialStaff.value?.length ? specialStaff.value : null,
        is_blocked: blockedStaff.value ?? null,
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        ...filters.value
      }).then(() => {

        if (specialStaff.value?.length) {
           hidePagination.value = true;
           store.dispatch('users/clearSpecialStaff');
        } else if (hidePagination.value) {
           hidePagination.value = false;
        }

        store.dispatch('users/clearShowBlocked');

        tableLoading.value = false;
      });
    }

    const onSelectChange = selectedRowKeys => {
      selectedUsers.value = selectedRowKeys;
    };

    const addNewUser = () => {
      currentAction.value = 'add_user';
      showActionModal.value = true;
    }

    const handleAction = (action, userObject = null) => {
      if (userObject) {
        currentActionUser.value.id = userObject.id;
        currentActionUser.value.groups = userObject.groups.map((group) => group.id);
        currentActionUser.value.name = userObject.name;
      } else {
        currentActionUser.value = null;
      }

      if (action === 'edit') {
        router.push('/staff/' + userObject.id + '/edit');
        return;
      }

      if (action !== 'export') {
        currentAction.value = action;
        showActionModal.value = true;
      }
    }

    const clearCurrentUserAndAction = () => {
      currentAction.value = '';
      actionToApply.value = null;
      showActionModal.value = false;
      currentActionUser.value = {
        id: null,
        name: '',
        groups: [],
      }
    }

    // фильтры
    const openFilterModal = () => {
      showFilterModal.value = true;
    }

    const updateFilters = () => {
      showMobileFilterModal.value = false;

      currentPage.value = 1;
      fetchUsers();
    }

    const resetFilters = () => {
      store.dispatch('users/setUserFilters', {
        filters: {
          name: '',
          job_title: '',
          department: '',
          online_from: '',
          created_at: '',
          email: '',
          telephone: '',
          groups: [],
          without_group_only: false,
        },
        filterString: null,
      });

      currentPage.value = 1;
      fetchUsers();
    }

    const handleGroupFilter = () => {
       if (groupFilter.value === 'Все') {
         additionalFilters.groups = [];
         additionalFilters.without_group_only = false;
       } else if (groupFilter.value === 'Без группы') {
         additionalFilters.groups = [];
         additionalFilters.without_group_only  = true;
       } else {
         additionalFilters.without_group_only = false;

         store.dispatch('users/setUserFilters', {
           filters: {...filters.value,
             without_group_only: additionalFilters.without_group_only,
             groups: additionalFilters.groups,
           },
           filterString: filterString,
         });

         return;
       }

       store.dispatch('users/setUserFilters', {
         filters: {...filters.value,
           without_group_only: additionalFilters.without_group_only,
           groups: additionalFilters.groups,
         },
         filterString: filterString,
       });

       currentPage.value = 1;
       fetchUsers();
    }

    const handleGroupSelect = () => {
      fetchUsers();
    }

    //сортировка
    const sortState = ref({
      field: null,
      order: null,
    });

    const handleTableChange = (pagination, filter, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      };

      fetchUsers();
    }

    // пагинация
    const retrieveEntitiesPerPage = () => {
      let storageValue = localStorage.getItem('perPage');
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10;
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
          entitiesPerPage.value = perPage;
          setEntitiesPerPage(entitiesPerPage.value);
      }

      currentPage.value = current;
      fetchUsers();
    }

    onBeforeMount(() => {
      store.dispatch("group/getGroups").then(() => {
        groups.value = allGroups.value.map((group) => {
          return {
            id: group.id,
            name: group.name,
          }
        })

        groupsLoading.value = false;
      });

      retrieveEntitiesPerPage();
      fetchUsers();
    })

    watch(() => groupFilter.value, () => {
      handleGroupFilter();
    });

    watch(filters, () => {
          updateFilters();
        },
        { deep: true, immediate: true }
    );

    const isMobile = ref(window.innerWidth < 768)

    const onResize = () => {
      isMobile.value = window.innerWidth < 768
    }

    onMounted(() => {
      window.addEventListener('resize', onResize)
    });

    const fetchAfterSubmit = () => {
      showActionModal.value = false;
      clearCurrentUserAndAction();
      fetchUsers();
    };

    onBeforeUnmount(() => {
      store.dispatch('users/clearUserFilters');
    });

    return {
      router,
      NotFoundIcon,
      currentPage,
      entitiesPerPage,
      columns,
      groupFilterData,
      groupFilter,
      applyToAll,
      actionToApply,
      actions,
      groups,
      filters,
      selectedUsers,
      onSelectChange,
      resetFilters,
      addNewUser,
      tableLoading,
      showFilterModal,
      openFilterModal,
      groupsLoading,
      updateFilters,
      filtersPopoverOpen,
      userData,
      currentActionUser,
      handleAction,
      totalUsers,
      handlePageChange,
      showMobileFilterModal,
      handleTableChange,
      handleGroupFilter,
      handleGroupSelect,
      showActionModal,
      modalActionLoading,
      currentAction,
      groupToCopyIn,
      isMobile,
      passwordResetForm,
      clearCurrentUserAndAction,
      statusError,
      fetchUsers,
      hidePagination,
      showOrderForm,
      fetchAfterSubmit,
      additionalFilters,
      filterString
    }
  }
}
</script>

<style scoped lang="scss">
  .card {
    min-height: 75vh;
  }

  .view-container {
    padding-top: 0;
    padding-bottom: 0;
  }

  .bottom-container {
    width: 100%;
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: 15px;

    @media (max-width: 768px) {
      flex-direction: column;
      justify-content: center;
    }
  }

  :deep(.card .ant-table-content) {
    min-height: 60vh;
  }

  .filter-block {
    display: flex;
    column-gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    row-gap: 15px;
  }

  :deep(.ant-table-wrapper .ant-table-thead >tr>th) {
    padding: 5px;
  }

  :deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td) {
    padding: 10px 5px !important;
  }

  .action-block {
    display: flex;
    align-items: center;
    column-gap: 15px;

    @media (max-width: 550px) {
      width: 100%;
    }
  }

  .action-select-wrapper {
    min-width: 180px;

    @media (max-width: 550px) {
      width: 100%;

      .ant-select {
        width: 100%;
      }
    }
  }

  .action-checkbox-wrapper {
    min-width: 200px;
  }

  .no-data-text {
    color: var(--main-font);
  }

  .add-button {
    margin-left: auto;

    @media (max-width: 550px) {
      width: 100%;

      button {
        width: 100%;
      }
    }
  }

  .search-tool-block {
    @media (max-width: 550px) {
      width: 100%;
    }
  }

  .search-tool-block .ant-select {
    width: 350px;

    @media (max-width: 550px) {
      width: 100%;
    }
  }

  .extended-filter-block {
    display: none;

    @media (max-width: $mobile-breakpoint) {
      margin: 24px 0;
      display: flex;
      column-gap: 25px;
      align-items: center;
      justify-content: space-between;
    }
  }

  .extended-filter-block h2 {
    font-size: 20px;
    margin-bottom: 0;
  }

  .extended-search-block-mobile {
    display: none;
    margin-right: 10px;

    @media(max-width: 768px) {
      display: block;
    }
  }

  :deep(.anticon-reload) {
    cursor: pointer;
  }

  :deep(.anticon-reload:hover) {
    color: var(--main-green) !important;
  }

  .add-button button {
    background-color: var(--main-green) !important;
    color: #fff;
  }

  .add-button button:hover {
    color: #fff;
    border-color: var(--polar-green-6);
  }

  .no-data-wrapper {
    height: 50vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 5px;
    padding: 24px;
  }

  .no-data-wrapper button {
    margin-top: 10px;
  }

  .no-data-wrapper button:hover {
    border-color: var(--main-green);
    color: var(--main-green);
  }

  @media (max-width: 768px) {
    :deep(.ant-pagination .ant-pagination-total-text ) {
      display: none !important;
    }
  }

  :deep(.ant-input-focused) {
    border-color: var(--main-green);
  }

  .ant-input:hover,
  .ant-input:focus {
    border-color: var(--main-green);;
  }

  .ant-input:focus,
  .ant-select-focused .ant-select-selector {
    box-shadow: 0 0 0 2px var(--polar-green);
  }

  .ant-input-affix-wrapper:hover,
  .ant-input-affix-wrapper-focused {
    border-color: var(--main-green);
  }

  .ant-input-affix-wrapper-focused {
    box-shadow: 0 0 0 2px var(--polar-green);
  }

  .filter-button {
    background-color: var(--main-green);
    color: #fff;
  }

  .filter-button:hover {
    background-color: var(--main-green);
    border-color: var(--polar-green-6);
    color: #fff;
  }

  .filter-button:active,
  .add-button button:active {
    background-color: var(--main-green);
    border-color: var(--polar-green-6);
    color: #fff;
  }

  .filter-cancel-button:hover,
  .filter-cancel-button:active {
    color: var(--dist-red-main);
    border-color: var(--dist-red-main);
  }

  .search-outline-block {
    position: relative;
  }

  .dot {
    position: absolute;
    top: -1px;
    left: 11px;
    height: 5px;
    width: 5px;
    background-color: var(--color-error);
    border-radius: 50%;
  }

  .staff-link {
    color: var(--main-green);
  }

  .staff-link:hover {
    color: var(--polar-green-3);
  }

  :deep(.ant-btn-text.ant-dropdown-trigger) {
    color: lightgrey;
  }

  :deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
    color: var(--main-font);
  }

  :deep(.ant-table-tbody .ant-dropdown-trigger) {
    padding: 4px 5px !important;
  }

  :deep(.ant-table-wrapper .ant-table-tbody >tr.ant-table-row-selected >td) {
    background-color: var(--light-grey);
  }

  :deep(.ant-table-wrapper .ant-table-tbody >tr.ant-table-row-selected:hover>td) {
    background-color: var(--light-grey);
  }

  :deep(.action-modal) {
    top: auto !important;
    bottom: 0;
    margin: 0;
    transform: none !important;
  }

  .ant-modal-root .action-modal {
    top: auto !important;
    bottom: 0;
    margin: 0;
    transform: none !important;
  }

  :deep(.action-modal .ant-modal-content) {
    border-radius: 16px 16px 0 0;
  }

  .action-buttons {
    @media (max-width: 768px) {
      .ant-row {
        flex-direction: column;
        row-gap: 10px;
      }

      .ant-row button{
        width: 100%
      }
    }
  }

  .modal-text {
    font-size: 16px;
  }

  .reset-password-form {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  .password-radio-group {
    display: flex;
    flex-direction: column;
    row-gap: 5px;
  }

  :deep(.ant-tag) {
    margin-bottom: 5px;
  }

  :deep(.ant-divider-horizontal) {
    margin: 16px 0 !important;
  }

  h2 {
    font-weight: 600;
    font-size: 16px;
  }

  :deep(.manage_access) {
    width: 800px !important;
  }
</style>